import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CustomList from "../../../../components/shared/List";
import { PaginationContext } from "../../../../components/shared/pagination/PaginationContext";
import { getFilterMeta } from "./FilterMeta";
import { withApollo } from "react-apollo";
import { LIST_USERS_DISPLAYS, LIST_USERS_FILTERS } from "../../../../gql/bookingapi";
import { OwcPagination, OwcProgressSpinner } from "@one/react";
import BackdropPortal from "../../../../components/shared/BackdropPortal";
import { useSelector } from "react-redux";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { DURATION } from "../../../../constants";
import BulkRemovalSummary from "../../components/BulkRemovalSummary";
import BulkRemovalDialog from "../../components/BulkRemovalDialog";
import { removeFilterAssignment } from "../removeFilterAssignment";
import { hasActiveSearchQuery } from "../../components/tableMetaHelpers";

export const emptyValueSign = "-";

const buildColumnSetRef = (ownerId, columnSetId) => {
  if (!ownerId || !columnSetId) {
    return null;
  }

  return `${ownerId}::${columnSetId}`;
};

const getColumnSetIdFromDisplay = (display) => {
  if (!display?.displayName) {
    return null;
  }

  return `EQUIPMENTLIST#${display.displayName}`;
};

const getDisplayNameFromColumnSetId = (columnSetId) => {
  if (!columnSetId || typeof columnSetId !== "string") {
    return "";
  }

  const [, displayName = ""] = columnSetId.split("#");
  return displayName;
};

const FilterContent = ({
  client,
  registerUpdateFn,
  registerTriggerFetch,
  showMyFilters = false,
  searchAssignedGroup = "",
}) => {
  const { fetching } = useContext(PaginationContext);
  const [filtersList, setFiltersList] = useState([]);
  const [allFiltersList, setAllFiltersList] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [showPerPage, setShowPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const [triggerRefresh, setTriggerRefresh] = useState(() => () => { });
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const storeUser = useSelector((store) => store.user);

  const fetchFilters = useCallback(async () => {
    setLoading(true);
    try {
      const [filtersResponse, displaysResponse] = await Promise.all([
        client.query({
          query: LIST_USERS_FILTERS,
          variables: { siteName: storeUser.site },
          fetchPolicy: 'network-only',
        }),
        client.query({
          query: LIST_USERS_DISPLAYS,
          variables: { siteName: storeUser.site, limit: 1000 },
          fetchPolicy: 'network-only',
        }),
      ]);

      const allFilters = filtersResponse?.data?.listAllUsersFilters ?? [];
      const allDisplays = displaysResponse?.data?.listAllUsersDisplays ?? [];

      const displaysByReference = new Map(
        allDisplays
          .map((display) => {
            const columnSetId = getColumnSetIdFromDisplay(display);
            const ref = buildColumnSetRef(display?.userID, columnSetId);
            return [ref, display];
          })
          .filter(([ref]) => Boolean(ref))
      );

      const assignmentRows = [];

      for (const filter of allFilters) {
        if (!filter.sharedWithGroups || !filter.sharedWithGroups.length) continue;

        const displayReference = buildColumnSetRef(filter.columnSetUserID, filter.columnSetId);
        const matchingDisplay = displayReference ? displaysByReference.get(displayReference) : null;
        const fallbackDisplayName = getDisplayNameFromColumnSetId(filter.columnSetId);

        assignmentRows.push({
          ...filter,
          columnSetOwnerId: filter.columnSetUserID ?? matchingDisplay?.userID ?? null,
          columnSet: matchingDisplay?.displayName || fallbackDisplayName || emptyValueSign,
          assignedGroups: filter.sharedWithGroups.join(", "),
          key: filter.id,
        });
      }
      console.log('Fetched assignmentRows:', assignmentRows);
      const visibleRows = showMyFilters
        ? assignmentRows.filter(
          item => item.userID === storeUser.id || item.columnSetOwnerId === storeUser.id
        )
        : assignmentRows;

      setAllFiltersList(visibleRows);
    } catch (error) {
      console.log("Failed to fetch filters:", error);
      setAllFiltersList([]);
      setFiltersList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMyFilters]);

  useEffect(() => {
    if (registerTriggerFetch) {
      registerTriggerFetch(fetchFilters);
    }
    setTriggerRefresh(() => fetchFilters); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerTriggerFetch, fetchFilters]);

  useEffect(() => {
    fetchFilters(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, showMyFilters]);

  const filteredFiltersList = useMemo(() => {
    const query = searchAssignedGroup.trim().toLowerCase();
    if (!query) {
      return allFiltersList;
    }

    return allFiltersList.filter((item) => {
      const groups = item.sharedWithGroups || [];
      const assignedGroups = (item.assignedGroups || "").toLowerCase();

      return groups.some((group) => (group || "").toLowerCase().includes(query)) || assignedGroups.includes(query);
    });
  }, [allFiltersList, searchAssignedGroup]);

  useEffect(() => {
    setPageNo(1);
  }, [searchAssignedGroup]);

  const paginate = (source) => {
    setVisible(false);
    setLoading(true);
    setTimeout(() => {
      const lowIndex = (pageNo - 1) * showPerPage;
      const highIndex = lowIndex + showPerPage;
      setFiltersList(source.slice(lowIndex, highIndex));
      setVisible(true);
      setLoading(false);
    })
  };

  useEffect(() => {
    setTotalCount(filteredFiltersList.length);
    paginate(filteredFiltersList); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, showPerPage, filteredFiltersList]);

  const updateSharedGroups = (filterId, newGroups) => {
    setAllFiltersList(prev => {
      return prev.map(f =>
        `EQUIPMENTLIST#${f.filterName}` === filterId
          ? {
            ...f,
            sharedWithGroups: newGroups,
            assignedGroups: newGroups?.length ? newGroups.join(", ") : emptyValueSign,
            modifiedBy: storeUser.name || storeUser.email || storeUser.id
          }
          : f
      );
    });
  };

  useEffect(() => {
    if (typeof registerUpdateFn === 'function') {
      registerUpdateFn(updateSharedGroups);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowsToRemove = filteredFiltersList;
  const shouldShowBulkRemovalSummary = hasActiveSearchQuery(searchAssignedGroup);

  const handleBulkDelete = async () => {
    if (!rowsToRemove.length) {
      return;
    }

    setIsBulkDeleteOpen(false);
    setIsBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        rowsToRemove.map((item) =>
          removeFilterAssignment({
            client,
            filterName: item.filterName,
            filterUserID: item.userID,
            columnSetName: item.columnSet,
            columnSetUserID: item.columnSetOwnerId || item.userID,
            groupIds: item.sharedWithGroups,
          })
        )
      );

      const deletedCount = results.filter((result) => result.status === "fulfilled").length;
      const failedCount = results.length - deletedCount;

      if (deletedCount > 0) {
        snackbarService.show({
          message: `${deletedCount} assignment${deletedCount > 1 ? "s" : ""} removed successfully.`,
          type: failedCount ? "warning" : "success",
          duration: DURATION,
        });
      }

      if (failedCount > 0) {
        snackbarService.show({
          message: `Failed to remove ${failedCount} assignment${failedCount > 1 ? "s" : ""}.`,
          type: "error",
          duration: DURATION,
        });
      }
    } finally {
      setIsBulkDeleting(false);
      fetchFilters();
    }
  };

  const bulkRemoveMeta = {
    fields: {
      assignedGroups: {
        text: "Assigned groups",
      },
      filterName: {
        text: "Filter",
      },
      columnSet: {
        text: "Column set",
      },
    },
  };

  const filterMeta = getFilterMeta(triggerRefresh, setTriggerRefresh);

  return (
    <>
      {loading ? <div style={{ minHeight: '40px' }}>
        <OwcProgressSpinner />
      </div> : null}
      {visible && (
        <CustomList
          meta={filterMeta}
          data={filtersList}
          fieldArray={[]}
          onRequestSort={false}
          isReverseOrder={true}
          orderBy="name"
          loading={fetching}
        />
      )}
      <BackdropPortal show={isBulkDeleting} />
      <BulkRemovalDialog
        open={isBulkDeleteOpen}
        onClose={() => setIsBulkDeleteOpen(false)}
        rows={rowsToRemove}
        onConfirm={handleBulkDelete}
        loading={isBulkDeleting}
        summaryLabel="assignment"
        tableMeta={bulkRemoveMeta}
        mapRow={(item) => ({
          ...item,
          filterName: item.filterName || emptyValueSign,
          columnSet: item.columnSet || emptyValueSign,
        })}
      />
      <div
        style={{
          marginTop: -16,
          display: "grid",
          gridTemplateColumns: shouldShowBulkRemovalSummary ? "auto 1fr" : "1fr",
          alignItems: "center",
          gap: 16,
        }}
      >
        {shouldShowBulkRemovalSummary && (
          <BulkRemovalSummary
            rowsCount={rowsToRemove.length}
            onBatchRemovalClick={() => setIsBulkDeleteOpen(true)}
          />
        )}
        <OwcPagination
          style={{ justifySelf: "end" }}
          onPageChange={(e) => {
            setShowPerPage(e.detail.rowsPerPage);
            setPageNo(e.detail.page);
          }}
          options={[5, 10, 20, 50]}
          rowsPerPage={showPerPage}
          total={Math.ceil(totalCount / showPerPage)}
        >
          <span slot="rows-per-page">Rows per page</span>
        </OwcPagination>
      </div>

    </>
  );
};

export default withApollo(FilterContent);
